// tslint:disable:max-line-length
import { connect } from 'react-redux';
import * as RX from 'reactxp';
import ButtonLink from '../../components/button_link';
import ExternalLink from '../../components/external_link';
import OutlineBox from '../../components/outline_box';
import * as ReactGA from '../../modules/google_analytics';
import { openUrl } from '../../modules/links';
import t from '../../utils/i18n';
import { mapStateToProps } from '../../utils/mapper';
import { body, colors, h1, h2, h3, subHeader } from '../../utils/styles';
import { urls } from '../../utils/urls';

import { applyBreakpoints } from '../../utils/breakpoints';

import { Link } from '../../modules/routing';

interface ResultsProps {
  symptom: {
    diagnosisScore: number;
    unappliedSymptoms?: string;
    text?: string[];
  };
  symptomChecker: () => void;
  dispatch: (action: any) => void;
  window: { width: number; multiplier: number };
}

const styles = (multiplier) => ({
  description: RX.Styles.createTextStyle({
    color: colors.black,
  }),
  disclaimer: RX.Styles.createTextStyle({
    color: colors.alert,
    fontWeight: 'bold',
  }),
  header: RX.Styles.createTextStyle({
    color: colors.header,
    marginBottom: 12 * multiplier,
    textAlign: 'left',
  }),
  level: RX.Styles.createTextStyle({
    color: colors.orange,
    fontWeight: '500',
    marginBottom: 12 * multiplier,
  }),
  sectionHeader: RX.Styles.createTextStyle({
    marginBottom: 12 * multiplier,
    marginTop: 0,
  }),
  serevityWrapper: RX.Styles.createViewStyle({
    flexDirection: 'row',
  }),
  subHeader: RX.Styles.createTextStyle({
    marginBottom: 12 * multiplier,
  }),
  subHeaderEmergency: RX.Styles.createTextStyle({
    color: colors.header,
    fontSize: 18 * multiplier,
    marginBottom: 12 * multiplier,
    textAlign: 'left',
  }),
});

interface OptionBoxType {
  buttonText: string;
  description: string;
  destination?: string;
  header: string;
  onPress?: () => void;
  external?: boolean;
  multiplier: number;
}

const OptionBox = ({
  header,
  description,
  buttonText,
  external = false,
  destination,
  multiplier,
}: OptionBoxType) => (
  <RX.View style={{ padding: 16 * multiplier }}>
    <RX.View style={{ paddingBottom: 16 }}>
      <RX.Text style={[h2(multiplier), styles(multiplier).sectionHeader]}>
        {header}
      </RX.Text>
      <RX.Text style={[body(multiplier), styles(multiplier).description]}>
        {description}
      </RX.Text>
    </RX.View>
    {external ? (
      <ExternalLink
        text={buttonText}
        button
        results
        locText={t('external.scheduling')}
        {...{ destination }}
      />
    ) : (
      <ButtonLink results text={buttonText} dark {...{ destination }} />
    )}
  </RX.View>
);
const EmergencyDepartment = ({ multiplier, width }) => {
  const emergencyDepartmentStyle = [
    RX.Styles.createViewStyle({
      flexDirection: 'column',
      marginBottom: 12 * multiplier,
      overflow: 'visible',
      padding: 16 * multiplier,
    }),
    ...applyBreakpoints(width, {
      md: { flexDirection: 'row', justifyContent: 'space-between' },
    }),
  ];

  return (
    <RX.View style={emergencyDepartmentStyle}>
      <RX.View style={{ maxWidth: 1100 * multiplier }}>
        <RX.Text style={[h2(multiplier), styles(multiplier).sectionHeader]}>
          {t('results.emergencyDepartment.header')}
        </RX.Text>
        <RX.Text
          style={[
            body(multiplier),
            styles(multiplier).description,
            { marginBottom: 20 },
          ]}
        >
          {t('results.emergencyDepartment.textBefore')}
          <RX.Link
            onPress={() => {
              ReactGA.trackEvent('Call 911', 'Navigation');
              openUrl('tel:911', '_blank');
            }}
            style={{ textDecorationLine: 'underline', color: colors.primary }}
            url={`tel:911`}
          >
            {t('results.emergencyDepartment.emergencyNumber')}
          </RX.Link>
          {t('results.emergencyDepartment.textAfter')}
        </RX.Text>
        <ButtonLink
          text={t('results.emergencyDepartment.button')}
          dark
          destination='/emergency-department'
        />
      </RX.View>
    </RX.View>
  );
};

const generateUsedSymptoms = (unappliedSymptoms, text) => {
  let symptoms = '';
  text &&
    text.map((symptom) => {
      if (
        !unappliedSymptoms ||
        !unappliedSymptoms.includes(` ${symptom.toLowerCase()}`)
      ) {
        symptoms += symptoms.length > 0 ? ', ' + symptom : symptom;
      }
    }); //tslint:disable-line

  return symptoms;
};

const generateUnusedSymptoms = (unappliedSymptoms, text) => {
  let symptoms = '';
  text &&
    text.map((symptom) => {
      if (
        unappliedSymptoms &&
        unappliedSymptoms.includes(` ${symptom.toLowerCase()}`)
      ) {
        symptoms += symptoms.length > 0 ? ', ' + symptom : symptom;
      }
    }); //tslint:disable-line

  return symptoms;
};

function saveTolocalStorage(state) {
  try {
    const serializeState = JSON.stringify(state);
    localStorage.setItem('state', serializeState);
  } catch (e) {
    // tslint:disable-next-line:no-console
    console.log(e);
  }
}

function loadFromLocalStorage() {
  try {
    const serializeState = localStorage.getItem('state');
    if (serializeState === null) {
      return undefined;
    }
    return JSON.parse(serializeState);
  } catch (e) {
    return undefined;
  }
}

class Results extends RX.Component<ResultsProps, RX.Stateless> {
  constructor(props: ResultsProps) {
    if (props.symptom.diagnosisScore !== 0) {
      saveTolocalStorage(props);
      super(props);
    } else {
      props.dispatch({ type: 'SUBMITTED' });
      props.dispatch({ type: 'WHERE_TO', ...loadFromLocalStorage().symptom });
      openUrl('/results', '_self');
    }
  }

  public componentDidMount() {
    ReactGA.trackEvent('Go to Results', 'Navigation');
    const {
      symptom: { text, unappliedSymptoms },
    } = this.props;

    text &&
      text.map((symptom) => {
        if (
          unappliedSymptoms &&
          unappliedSymptoms.indexOf(symptom.toLowerCase()) !== -1
        ) {
          ReactGA.trackEvent('Symptoms', 'Unused', { label: symptom });
        }
      }); //tslint:disable-line
  }

  public componentWillUnmount() {
    this.props.dispatch({ type: 'CLEAR_SCROLL' });
  }

  public render() {
    const {
      dispatch,
      symptom: { diagnosisScore, unappliedSymptoms, text },
      window: { width, multiplier },
    } = this.props;
    const isabellaEdThreshold = 80;
    const ed = diagnosisScore >= isabellaEdThreshold;
    const linkStyle =
      RX.Platform.getType() !== 'web'
        ? {
            maxWidth: 200 * multiplier,
            paddingBottom: 12 * multiplier,
            paddingTop: 12 * multiplier,
          }
        : {
            fontSize: 16 * multiplier,
            maxWidth: 200 * multiplier,
            paddingBottom: 12 * multiplier,
            paddingTop: 12 * multiplier,
          };
    return (
      <RX.ScrollView>
        <RX.Text style={[h1(multiplier), styles(multiplier).header]}>
          {t(ed ? 'results.header' : 'results.header')}
        </RX.Text>
        <RX.View
          style={[
            styles(multiplier).serevityWrapper,
            {
              marginBottom: 12,
              width: width - 64 > 1200 ? 1200 : width - 64 * multiplier,
            },
          ]}
        >
          <RX.Text
            style={[
              body(multiplier),
              {
                width: width - 64 > 1200 ? 1200 : width - 64 * multiplier,
              },
            ]}
          >
            {t('results.basedResponses')}
            <RX.Text style={subHeader(multiplier)}>
              {generateUsedSymptoms(unappliedSymptoms, text)}
            </RX.Text>
            {t('results.recommended')}
          </RX.Text>
        </RX.View>
        {unappliedSymptoms && (
          <RX.View
            style={[
              styles(multiplier).serevityWrapper,
              {
                marginBottom: 12,
                width: width - 64 > 1200 ? 1200 : width - 64 * multiplier,
              },
            ]}
          >
            <RX.Text
              style={[
                body(multiplier),
                {
                  width: width - 64 > 1200 ? 1200 : width - 64 * multiplier,
                },
              ]}
            >
              {t('results.pleaseNote')}
              <RX.Text
                style={[
                  subHeader(multiplier),
                  { fontStyle: 'italic', fontWeight: 'normal' },
                ]}
              >
                {' '}
                {generateUnusedSymptoms(unappliedSymptoms, text)}
              </RX.Text>
            </RX.Text>
          </RX.View>
        )}
        <OutlineBox>
          {!ed && (
            <OptionBox
              external
              header={t('results.primaryCare.header')}
              description={t('results.primaryCare.text')}
              destination={urls.primaryCare}
              buttonText={t('results.primaryCare.button')}
              {...{ multiplier }}
            />
          )}
          {!ed && (
            <OptionBox
              external
              header={t('results.urgentCare.header')}
              description={t('results.urgentCare.text')}
              destination={urls.urgentCare}
              buttonText={t('results.urgentCare.button')}
              {...{ multiplier }}
            />
          )}
          {!ed && (
            <OptionBox
              external
              header={t('results.onDemand.header')}
              description={t('results.onDemand.text')}
              destination={urls.onDemand}
              buttonText={t('results.onDemand.button')}
              {...{ multiplier }}
            />
          )}
        </OutlineBox>
        {!ed && (
          <RX.View>
            <RX.Text
              style={[
                styles(multiplier).subHeaderEmergency,
                { marginBottom: 7, marginTop: 30 },
              ]}
            >
              {t('results.emergencyDepartment.mainHeader')}
            </RX.Text>
          </RX.View>
        )}
        <OutlineBox>
          <EmergencyDepartment {...{ multiplier, width }} />
        </OutlineBox>
        <RX.View>
          <Link
            onClick={() => {
              dispatch({ type: 'MODIFYING', modify: true });
            }}
            onPress={() => {
              dispatch({ type: 'MODIFYING', modify: true });
            }}
            to='/symptom-checker'
            style={linkStyle}
          >
            <RX.Text style={{ textDecorationLine: 'underline' }}>
              {t('results.modify')}
            </RX.Text>
          </Link>
          <Link
            to='/symptom-checker'
            style={linkStyle}
            onClick={() => {
              dispatch({ type: 'CLEAR_VALUES' });
            }}
            onPress={() => {
              dispatch({ type: 'CLEAR_VALUES' });
            }}
          >
            <RX.Text style={{ textDecorationLine: 'underline' }}>
              {t('results.startOver')}
            </RX.Text>
          </Link>
        </RX.View>

        <RX.View style={{ marginTop: 32 * multiplier }}>
          <RX.Text
            style={{
              color: colors.grey,
              fontSize: 12 * multiplier,
              fontStyle: 'italic',
            }}
          >
            {t('results.disclaimer')} {t('results.disclaimerText')}
          </RX.Text>
        </RX.View>
      </RX.ScrollView>
    );
  }
}

export default connect(mapStateToProps)(Results);



// WEBPACK FOOTER //
// ./src/pages/results/index.tsx